import _ from 'underscore';
import Promise from 'bluebird';
import { useCallback, useState } from 'react';

import { BaseResultType } from '../types';

const DEBOUNCE_DELAY = 300;

export interface UsePaginatedFetchingStateProps<ResultType> {
  fetchFn: (query: string, pageNumber: number, pageSize: number) => Promise<{ results: ResultType[], totalCount: number } | ResultType[]>;
  fetchOnEmptyInput: boolean;
  pageSize: number;
}

export type FetchingStatus = 'idle' | 'loading' | 'loaded';

export interface PaginatedFetchingState<ResultType> {
  status: FetchingStatus;
  results: ResultType[];
  currentPage: number;
  totalPages: number;
  hasMore: boolean;
}

export const usePaginatedFetchingState = <ResultType extends BaseResultType>({
  fetchFn,
  fetchOnEmptyInput,
  pageSize,
}: UsePaginatedFetchingStateProps<ResultType>) => {
  const [state, setState] = useState<PaginatedFetchingState<ResultType>>({
    status: 'idle',
    results: [],
    currentPage: 1,
    totalPages: 1,
    hasMore: false,
  });

  const fetch = useCallback((query, page = 1) => {
    if (state.status === 'loading') {
      return;
    }

    if (!query && !fetchOnEmptyInput) {
      setState({
        status: 'idle', results: [], currentPage: 1, totalPages: 1, hasMore: false,
      });
      return;
    }

    setState(prevState => ({ ...prevState, status: 'loading' }));

    fetchFn(query, page, pageSize).then((response) => {
      const { results, totalCount } = Array.isArray(response) ? { results: response, totalCount: response.length } : response;
      const totalPages = Math.ceil(totalCount / pageSize);
      setState(preState => ({
        ...preState,
        status: 'loaded',
        results: page === 1 ? results : [...preState.results, ...results],
        currentPage: page,
        totalPages,
        hasMore: page < totalPages,
      }));
    }).catch(() => {
      setState(prevState => ({ ...prevState, status: 'loaded' }));
    });
  }, [fetchFn, fetchOnEmptyInput, pageSize, state]);

  const fetchNextPage = useCallback((query) => {
    if (state.currentPage < state.totalPages) {
      fetch(query, state.currentPage + 1);
    }
  }, [fetch, state]);


  const onCleanUp = useCallback(() => {
    setState({
      status: 'idle', results: [], currentPage: 1, totalPages: 1, hasMore: true,
    });
  }, []);

  const debouncedFetch = _.debounce(fetch, DEBOUNCE_DELAY);

  return {
    status: state.status,
    results: state.results,
    hasMore: state.hasMore,
    fetch: debouncedFetch,
    fetchNextPage,
    onCleanUp,
  };
};
